// @givmo/board — a director moves their own seat to another Givmo account (CBG-SEC2-L4-MOVE;
// D-CBG-23 flow (b); the approved frames b2–b9c).
//
// Jonathan's answer 1 (2026-10-07 19:14 PDT, "i agree with your recs to the four questions btw"): a
// director may move their own seat to another Givmo account with BOTH proofs — signed in to the
// account the seat is on at the start, and the seat's address at the end (the other account's own
// confirmed email is that address, or a code mailed there). It is recorded in the seat's history,
// with a notice to both addresses. Nothing moves until the last button: the seat stays where it is
// through every step before it, so stopping halfway changes nothing.
//
// ONE ADDRESS, `/board/director/move`, in three phases the page reads off the tab and the account:
//
//   * THE START (b2, b2e), signed in to the account the seat is on, on a board where Board's verdict
//     says another account is needed (`?org=` names it, `?via=new|existing` the way the pointer's
//     button chose). What happens and what it costs, before anything is asked; Continue starts the
//     move — a single-use ticket, live 30 minutes, kept in THIS TAB only (`director/seat-move.ts`) —
//     and signs that account out on this browser. "Create new account" then opens the console's own
//     create form, with the move's two lines (b3c); "Move to another Givmo account" the ways in (b3).
//   * SIGNED OUT, holding the ticket (b3): the console's ways in without the organization door
//     (`another_account`, which would lead straight back), and "Create new account" under them.
//   * SIGNED IN TO ANOTHER ACCOUNT, holding the ticket: the preview names the board, the seat and
//     both accounts before the act. The account's address already proves it (b4m), or a code goes to
//     the seat's address, never to the account asking (b4, b5, b5w). Done, the seat is on this
//     account: a toast, and Account Security's setup offer next, as after joining (b6).
//
// THE ENDINGS. Signed back in with the account the seat is on (b9a), or an account another
// organization's lock binds too (b9b): the same two ways on. The ticket gone — 30 minutes, the seat
// changed under it, or this is another tab (b9c): nothing moved; start again from the board.
//
// Every new line is in the frames' words, approved by Jonathan in copy round CBG-SEC2-L4-COPY1
// (2026-10-12 13:49 PDT, "otherwise approved"), as written, and the no-email fallbacks in copy
// round CBG-YR3-ROLES-COPY4 (2026-10-13 12:09 PDT); the code step and a wrong code are the
// invitation's own (`director-steps.tsx`). CBG-FIX106 (the FIX104 copy page's rows 4.13 and 4.14,
// approved 2026-10-17 17:42 PDT): the start says a seat for someone who isn't on the board's
// committee where the board's says the board; the later pages, which know only the move, say one
// line true of either seat ("Your seat at {Organization} …").

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';
import { Button, ErrorMessage, Skeleton, testIdProp, toast } from '@givmo/design-system';
import { Link, boardPath, useNavigate, useSearch } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardBasePath, useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorOrgOf,
  seatCommitteeNames,
} from '../../director/director-board';
import {
  useBoardDirectorHost,
  type BoardDirectorSeatMoveFacts,
} from '../../director/director-host';
import { decodeDirectorBoard, directorBoardFor, useDirectorMe } from '../../director/director-me';
import {
  decodeSeatMovePreview,
  decodeSeatMoveTicket,
  forgetSeatMove,
  markCreateForSeatMove,
  maskEmailAddress,
  rememberSeatMove,
  seatMoveInTab,
  seatMoveViaOf,
  takeCreateForSeatMove,
  type SeatMoveVia,
  type StoredSeatMove,
} from '../../director/seat-move';
import {
  needsFactorSetup,
  readHoldsAuthenticator,
  useLatestStepUpHost,
} from '../../director/signing-gate';
import {
  DIRECTOR_REFUSALS,
  isCommitteeOnly,
  seatLinkUnavailableSentence,
  type DirectorBoard,
} from '../../director/director-wire';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { useDirectorFailureWords } from '../../director/access-refusal';
import { DirectorSetupOfferPage } from './director-act';
import { DirectorShell } from './director-shell';
import { CodeStep, STEP_BODY, STEP_LINK, StepCard, UseAnotherAccount } from './director-steps';

const SMALL = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const PAGE = 'page-director-move';

function refusalCode(error: unknown): string | null {
  return isGivmoError(error) ? (firstErrorCode(error) ?? null) : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * "Your seat at Harborlight Youth Arts", or "Your seat" where the name is unknown: the later pages
 * know only the move, never whose seat it is, so one line true of either (CBG-FIX106, row 4.14).
 */
function yourSeatAt(organizationName: string | null): string {
  return organizationName === null ? 'Your seat' : `Your seat at ${organizationName}`;
}

/**
 * Where "Not now" goes: the director page the pointer was pressed on, carried as `?back=`, when it is
 * one of this family's own addresses; else the board's home.
 */
export function seatMoveBackOf(value: unknown, basePath: string): string | null {
  const back = text(value);
  const family = boardPath(basePath, BOARD_DIRECTOR_PAGES.home);
  const move = boardPath(basePath, BOARD_DIRECTOR_PAGES.move);
  if (back === null || back.includes('//') || back.includes('\\')) return null;
  if (!(back === family || back.startsWith(`${family}/`) || back.startsWith(`${family}?`)))
    return null;
  return back.startsWith(move) ? null : back;
}

export function DirectorMovePage() {
  const search = useSearch();
  const host = useBoardDirectorHost();
  const orgParam = directorOrgOf(search);
  const via = seatMoveViaOf(search.via);
  // Continue was pressed: the start keeps its screen until the account is signed out, so the
  // ticket it just kept never shows this account the finish meanwhile.
  const [leaving, setLeaving] = useState(false);
  // The move this tab holds, read afresh: the start writes it and a sign-out remounts nothing. A
  // ticket for ANOTHER board doesn't answer a start for this one: the start replaces it.
  const stored = seatMoveInTab();
  const held = stored !== null && (orgParam === null || orgParam === stored.orgRef) ? stored : null;

  if (host.account === null) {
    return held === null ? <MoveEnded signedIn={false} /> : <SignedOutStep move={held} />;
  }
  if (held !== null && !leaving) return <FinishStep move={held} />;
  return (
    <StartStep
      orgRef={orgParam}
      via={via}
      onLeaving={() => setLeaving(true)}
      onLeft={() => setLeaving(false)}
    />
  );
}

// ── The start (b2, b2e) ─────────────────────────────────────────────────────────────────────────

function StartStep({
  orgRef,
  via,
  onLeaving,
  onLeft,
}: {
  orgRef: string | null;
  via: SeatMoveVia;
  /** Continue is signing this account out: hold this screen until it is. */
  onLeaving: () => void;
  /** The sign-out settled (a failed one leaves the account signed in, and the finish says so). */
  onLeft: () => void;
}) {
  const operations = useBoardDirectorOperations();
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const navigate = useNavigate();
  const router = useRouter();
  const basePath = useBoardBasePath();
  const search = useSearch();
  const here = useRouterState({ select: (state) => state.location.href });
  const meQuery = useDirectorMe();
  const board = directorBoardFor(meQuery.data, orgRef);
  const [starting, setStarting] = useState(false);
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const [error, setError] = useState<string | null>(null);
  // The board's verdict, as its pointer showed it: only then is there a move to start here.
  const movable = board?.another_account_required === true;

  // The words name the locking organization only once the account's own security read says so.
  useEffect(() => {
    if (movable) void readHoldsAuthenticator(stepUpHost);
  }, [movable, stepUpHost]);

  // No board named, or nothing to move on it from this account: its boards.
  const nothingToMove = orgRef === null || (board !== null && !movable);
  useEffect(() => {
    if (nothingToMove) void navigate({ to: BOARD_DIRECTOR_PAGES.home, replace: true });
  }, [nothingToMove, navigate]);

  if (meQuery.isError) {
    return <MoveFailed onRetry={() => void meQuery.refetch()} />;
  }
  // This account holds no seat on the board named (another tab's move, say): nothing to start.
  if (orgRef !== null && meQuery.isSuccess && board === null) return <MoveEnded signedIn />;
  if (board === null || !movable) {
    return (
      <DirectorShell title="Move your seat to another Givmo account" testId={PAGE}>
        <Skeleton className="h-84 w-full max-w-[440px] rounded-lg" testId="director-move-loading" />
      </DirectorShell>
    );
  }

  const workEmail = host.account?.email ?? 'this Givmo account';
  const who = host.lockingOrganizationName ?? 'Your organization';
  const seatAddress = text(board.email_of_record);
  const masked = seatAddress === null ? null : maskEmailAddress(seatAddress);

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      const { data } = await operations.directorStartBoardSeatMove({
        path: { org_ref: board.org_ref },
        throwOnError: true,
      });
      const ticket = decodeSeatMoveTicket(data);
      if (ticket === null) {
        setStarting(false);
        setError('We couldn’t start the move just now. Please try again.');
        return;
      }
      onLeaving();
      rememberSeatMove({
        token: ticket.token,
        expiresAt: ticket.expiresAt,
        orgRef: board.org_ref,
        organizationName: board.organization_name,
        maskedEmailOfRecord: ticket.maskedEmailOfRecord,
        via,
        // What tells a finished move from an ended one once the ticket is spent (the finish).
        seatId: board.board_member_id,
        startedBy: host.account?.uid ?? null,
      });
      // The page that comes back signed out opens the create form, or the ways in.
      if (via === 'new') markCreateForSeatMove();
      await host.signOut(here);
      setStarting(false);
      onLeft();
    } catch (caught) {
      setStarting(false);
      setError(failureWords(caught, 'We couldn’t start the move just now. Please try again.'));
    }
  };

  const notNow = () => {
    const back = seatMoveBackOf(search.back, basePath);
    if (back !== null) {
      router.history.push(back);
      return;
    }
    void navigate({ to: BOARD_DIRECTOR_PAGES.home, search: { org: board.org_ref } });
  };

  return (
    <DirectorShell
      title={
        via === 'new'
          ? 'Move your seat to a new Givmo account'
          : 'Move your seat to another Givmo account'
      }
      description={
        // CBG-FIX106 (row 4.13): a committee seat's own words.
        isCommitteeOnly(board)
          ? co.moveStartLead(who, workEmail, seatCommitteeNames(board))
          : `${who} manages how you sign in to ${workEmail}, so it can’t sign for this board. Another Givmo account can.`
      }
      identity={directorBoardIdentity(board)}
      testId={PAGE}
    >
      <StepCard testId="director-move-start">
        <ol className="flex flex-col gap-12" {...testIdProp('director-move-steps')}>
          <MoveStepItem number={1}>
            {via === 'new'
              ? `Create your new Givmo account, with an email address your ${host.lockingOrganizationNoun} doesn’t manage.`
              : 'Sign in with your other Givmo account.'}
          </MoveStepItem>
          <MoveStepItem number={2}>
            {masked === null
              ? 'We email a 6-digit code to the address on your seat.'
              : `We email a 6-digit code to ${masked}, the address on your seat.`}
          </MoveStepItem>
          {/* With no email, "this Givmo account" here is the approved fallback
              (CBG-YR3-ROLES-COPY4). */}
          <MoveStepItem number={3}>
            {`Your seat moves to the ${via === 'new' ? 'new' : 'other'} account. Everything you’ve signed stays on the record, and ${workEmail} ${
              isCommitteeOnly(board)
                ? co.moveStopsOpening(seatCommitteeNames(board))
                : 'stops opening this board'
            }.`}
          </MoveStepItem>
        </ol>
        <div className="flex flex-col gap-12 border-t border-border-subtle pt-16">
          {/* With no email, "this Givmo account" here is the approved fallback
              (CBG-YR3-ROLES-COPY4). */}
          <p className={SMALL} {...testIdProp('director-move-signs-out')}>
            Continuing signs {workEmail} out on this browser.
          </p>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={starting}
            onClick={() => void start()}
            testId="director-move-continue"
          >
            Continue
          </Button>
          {error !== null ? (
            <ErrorMessage testId="director-move-start-error">{error}</ErrorMessage>
          ) : null}
          <button
            type="button"
            className={`${STEP_LINK} self-center text-s13`}
            onClick={notNow}
            {...testIdProp('director-move-not-now')}
          >
            Not now
          </button>
        </div>
      </StepCard>
    </DirectorShell>
  );
}

function MoveStepItem({ number, children }: { number: number; children: string }) {
  return (
    <li className="flex items-start gap-12">
      <span
        aria-hidden
        className="flex size-24 shrink-0 items-center justify-center rounded-full bg-surface-sunken font-sans text-s13 font-w600 text-text-secondary"
      >
        {number}
      </span>
      <span className={STEP_BODY}>{children}</span>
    </li>
  );
}

// ── Signed out, holding the ticket (b3; b3c through the create form) ──────────────────────────────

function SignedOutStep({ move }: { move: StoredSeatMove }) {
  const host = useBoardDirectorHost();
  const here = useRouterState({ select: (state) => state.location.href });
  const facts: BoardDirectorSeatMoveFacts = {
    organizationName: move.organizationName,
    maskedEmailOfRecord: move.maskedEmailOfRecord,
  };
  const openCreateForm = () => void host.createAnotherAccount({ returnTo: here, move: facts });

  // "Create new account" from a signed-in step signed that account out back to here: open the form
  // now that the page is signed out. Once: the marker is spent on read.
  useEffect(() => {
    if (takeCreateForSeatMove()) openCreateForm();
    // `openCreateForm` closes over this render's facts; the marker keeps it to one call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DirectorShell
      title="Sign in with your other Givmo account"
      description={`${yourSeatAt(move.organizationName)} moves to it next. Use an account your ${host.lockingOrganizationNoun} doesn’t manage.`}
      testId={PAGE}
    >
      {host.renderWaysIn({ arrival: 'another_account', returnTo: here, move: facts })}
      <p
        className="w-full max-w-[440px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp('director-move-create')}
      >
        Don’t have one?{' '}
        <button type="button" className={STEP_LINK} onClick={openCreateForm}>
          Create new account
        </button>
      </p>
    </DirectorShell>
  );
}

// ── Signed in to another account, holding the ticket (b4, b4m, b5, b5w, b6, b9a, b9b) ────────────

/**
 * `linkUnavailable` (CBG-FIX161): Board couldn't link the seat just now, so the move didn't finish;
 * the step it was sent from stays, saying Board's sentence, and its act sends it again.
 */
type Step =
  | { kind: 'idle'; linkUnavailable?: string }
  | { kind: 'moving' }
  /** The finish asked for a code after all (the account's address stopped matching). */
  | { kind: 'code-ask'; masked: string; sending: boolean }
  | {
      kind: 'code-step';
      masked: string;
      failures: number;
      sentAt: number;
      submitting: boolean;
      throttled?: boolean;
      linkUnavailable?: string;
    }
  | { kind: 'code-unsent'; reason: 'skip' | 'failed'; masked: string }
  | { kind: 'same-account' }
  | { kind: 'another-account' }
  | { kind: 'already-seated' }
  | { kind: 'ended' }
  | { kind: 'failed' }
  | { kind: 'offering'; board: DirectorBoard | null };

function FinishStep({ move }: { move: StoredSeatMove }) {
  const operations = useBoardDirectorOperations();
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const navigate = useNavigate();
  const here = useRouterState({ select: (state) => state.location.href });
  const meQuery = useDirectorMe();
  const account = host.account;
  const [step, setStep] = useState<Step>({ kind: 'idle' });

  const previewQuery = useQuery({
    // The answer depends on who asks, so the account is part of the key.
    queryKey: ['board-director-seat-move', move.token, account?.email ?? null],
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorPreviewBoardSeatMove({
        body: { move_token: move.token },
        throwOnError: true,
      });
      return decodeSeatMovePreview(data);
    },
  });
  const preview = previewQuery.data ?? null;
  const masked = preview?.maskedEmailOfRecord ?? move.maskedEmailOfRecord;
  const organizationName = preview?.organizationName ?? move.organizationName;

  const endedOnPreview =
    previewQuery.isError &&
    refusalCode(previewQuery.error) === DIRECTOR_REFUSALS.seatMoveUnavailable;
  // Once the move has finished, its ticket is spent and the backend refuses it like any ended one.
  // A reload after the move (on b6's setup offer), or a finish whose answer was lost on the way
  // back, must never say nothing moved: when the seat the move started on is on THIS account's
  // `/me`, and this isn't the account the move started from, the move finished here.
  const mayHaveLanded =
    endedOnPreview &&
    !!move.seatId &&
    !!move.startedBy &&
    account?.uid !== undefined &&
    account.uid !== move.startedBy;
  const landedHere =
    mayHaveLanded && directorBoardFor(meQuery.data, move.orgRef)?.board_member_id === move.seatId;
  const ended =
    step.kind === 'ended' || endedOnPreview || (previewQuery.isSuccess && preview === null);
  // A ticket spent (moved) or gone (ended) is forgotten when this page is left, never while it shows
  // the result: the page reads the tab to know which step it is on.
  const spent = useRef(false);
  spent.current = spent.current || ended;
  useEffect(
    () => () => {
      if (spent.current) forgetSeatMove();
    },
    [],
  );
  // The move finished here before this page loaded: done, as after the finish (frame b6).
  useEffect(() => {
    if (!landedHere) return;
    // Frame b6, the toast.
    toast.success('Your seat is on this account now.');
    void navigate({ to: BOARD_DIRECTOR_PAGES.home, replace: true });
  }, [landedHere, navigate]);

  /** Sign this account out, back to the ways in (b3); the ticket stays in the tab. */
  const useAnotherAccount = () => void host.signOut(here);
  /** Sign this account out, and the page that comes back signed out opens the create form. */
  const createNewAccount = () => {
    markCreateForSeatMove();
    void host.signOut(here);
  };
  /** The backend says this request carried no sign-in: back to the ways in. */
  const signInEnded = () => void host.signOut(here);

  const moved = async (board: DirectorBoard | null) => {
    spent.current = true;
    // Frame b6, the toast.
    toast.success('Your seat is on this account now.');
    // As after joining: an account with neither factor meets Account Security's setup offer before
    // its board; one that has either goes straight on.
    const fresh = await meQuery.refetch();
    if (
      host.setupOffer !== null &&
      fresh.data?.signing.account_has_passkey === false &&
      needsFactorSetup(false, await readHoldsAuthenticator(stepUpHost))
    ) {
      setStep({ kind: 'offering', board });
      return;
    }
    await navigate({ to: BOARD_DIRECTOR_PAGES.home, replace: true });
  };

  const refused = (caught: unknown, fallback: Step): void => {
    switch (refusalCode(caught)) {
      case DIRECTOR_REFUSALS.seatMoveUnavailable:
        setStep({ kind: 'ended' });
        return;
      case DIRECTOR_REFUSALS.seatMoveSameAccount:
        setStep({ kind: 'same-account' });
        return;
      case DIRECTOR_REFUSALS.anotherAccountRequired:
        setStep({ kind: 'another-account' });
        return;
      case DIRECTOR_REFUSALS.alreadySeatedHere:
        setStep({ kind: 'already-seated' });
        return;
      case DIRECTOR_REFUSALS.signInRequired:
        signInEnded();
        return;
      default:
        setStep(fallback);
    }
  };

  const complete = async (code?: string) => {
    if (code === undefined) setStep({ kind: 'moving' });
    else
      setStep((current) =>
        current.kind === 'code-step'
          ? { ...current, submitting: true, linkUnavailable: undefined }
          : current,
      );
    try {
      const { data } = await operations.directorCompleteBoardSeatMove({
        body: code === undefined ? { move_token: move.token } : { move_token: move.token, code },
        throwOnError: true,
      });
      await moved(decodeDirectorBoard(data));
    } catch (caught) {
      switch (refusalCode(caught)) {
        case DIRECTOR_REFUSALS.claimCodeRequired: {
          const context = isGivmoError(caught) ? firstErrorContext(caught) : undefined;
          setStep({
            kind: 'code-ask',
            masked: text(context?.masked_email_of_record) ?? masked,
            sending: false,
          });
          return;
        }
        case DIRECTOR_REFUSALS.claimCodeFailed:
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, failures: current.failures + 1, submitting: false }
              : { kind: 'code-step', masked, failures: 1, sentAt: 0, submitting: false },
          );
          return;
        case DIRECTOR_REFUSALS.seatLinkUnavailable: {
          // CBG-FIX161: the move's ticket and a typed code stay good (nothing was moved or spent
          // but one attempt of the code), so the step stays, the code in its boxes, and says
          // Board's sentence; pressing the act again sends the same code.
          const said = seatLinkUnavailableSentence(caught, 'move');
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, submitting: false, linkUnavailable: said }
              : { kind: 'idle', linkUnavailable: said },
          );
          return;
        }
        default:
          refused(caught, { kind: 'failed' });
      }
    }
  };

  const requestCode = async () => {
    setStep((current) =>
      current.kind === 'code-ask'
        ? { ...current, sending: true }
        : current.kind === 'idle'
          ? { kind: 'code-ask', masked, sending: true }
          : current,
    );
    try {
      const { data } = await operations.directorRequestBoardSeatMoveCode({
        body: { move_token: move.token },
        throwOnError: true,
      });
      const row =
        typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
      const sentTo = text(row.masked_email_of_record) ?? masked;
      setStep(
        row.email_sent === true
          ? {
              kind: 'code-step',
              masked: sentTo,
              failures: 0,
              sentAt: Date.now(),
              submitting: false,
            }
          : { kind: 'code-unsent', reason: 'skip', masked: sentTo },
      );
    } catch (caught) {
      switch (refusalCode(caught)) {
        case DIRECTOR_REFUSALS.claimCodeThrottled:
          // The cap is reached, but the newest code sent still moves the seat: never a dead end
          // without the boxes to type it in.
          setStep((current) =>
            current.kind === 'code-step'
              ? { ...current, throttled: true }
              : {
                  kind: 'code-step',
                  masked,
                  failures: 0,
                  sentAt: 0,
                  submitting: false,
                  throttled: true,
                },
          );
          return;
        case DIRECTOR_REFUSALS.claimCodeNotSent:
          setStep({ kind: 'code-unsent', reason: 'failed', masked });
          return;
        default:
          refused(caught, { kind: 'failed' });
      }
    }
  };

  if (step.kind === 'offering') {
    // Moved: the header names the seat and the board it now holds here (frame b6).
    return (
      <DirectorSetupOfferPage
        variant="default"
        identity={directorBoardIdentity(step.board)}
        onSettled={() => void navigate({ to: BOARD_DIRECTOR_PAGES.home, replace: true })}
      />
    );
  }

  if (mayHaveLanded && meQuery.isError) {
    // Whether the move finished here can't be told without `/me`: never "nothing moved" on a guess.
    return (
      <MoveFailed
        onRetry={() => {
          void previewQuery.refetch();
          void meQuery.refetch();
        }}
      />
    );
  }
  if (mayHaveLanded && (meQuery.isPending || landedHere)) {
    // Asking `/me`, or leaving for the board because the move finished here.
    return (
      <DirectorShell title="Move your seat to this account" testId={PAGE}>
        <Skeleton className="h-84 w-full max-w-[440px] rounded-lg" testId="director-move-loading" />
      </DirectorShell>
    );
  }
  if (ended) return <MoveEnded signedIn />;

  if (previewQuery.isPending) {
    return (
      <DirectorShell title="Move your seat to this account" testId={PAGE}>
        <Skeleton className="h-84 w-full max-w-[440px] rounded-lg" testId="director-move-loading" />
      </DirectorShell>
    );
  }

  if (previewQuery.isError || preview === null || step.kind === 'failed') {
    return (
      <MoveFailed
        onRetry={() => {
          setStep({ kind: 'idle' });
          void previewQuery.refetch();
        }}
      />
    );
  }

  const email = account?.email ?? null;
  const signedInAs =
    email === null ? null : <span className="font-w600 text-text-strong">{email}</span>;

  // ── b9a, b9b: this account can't hold the seat ──
  const notThisAccount =
    step.kind === 'same-account' || step.kind === 'another-account'
      ? step.kind
      : preview.fromThisAccount
        ? 'same-account'
        : preview.anotherAccountRequired === true
          ? 'another-account'
          : null;
  if (notThisAccount !== null) {
    return (
      <DirectorShell
        title="Sign in with another Givmo account"
        description={`${yourSeatAt(organizationName)} moves to it next.`}
        testId={PAGE}
      >
        <StepCard testId={`director-move-${notThisAccount}`}>
          <p className={STEP_BODY}>
            {notThisAccount === 'same-account' ? (
              <>
                You’re signed in as {signedInAs}, the account your seat is moving from. Create a new
                Givmo account, or sign in with another one.
              </>
            ) : (
              <>
                You’re signed in as {signedInAs}. An organization manages how you sign in to this
                account too, so it can’t hold your seat either. Create a new Givmo account, or sign
                in with another one.
              </>
            )}
          </p>
          <TwoWaysOn onCreate={createNewAccount} onAnother={useAnotherAccount} />
        </StepCard>
      </DirectorShell>
    );
  }

  const movingFrom = preview.movingFromMasked;
  return (
    <DirectorShell
      title="Move your seat to this account"
      description={
        movingFrom === null
          ? `${yourSeatAt(organizationName)} moves here.`
          : `${yourSeatAt(organizationName)} moves here from ${movingFrom}.`
      }
      testId={PAGE}
    >
      {step.kind === 'already-seated' ? (
        <StepCard testId="director-move-already-seated">
          {/* Not drawn; b9's two ways on, for an account seated here. "This account", for an account
              with no email, is the approved fallback (CBG-YR3-ROLES-COPY4). CBG-FIX106 (row 4.14):
              "a seat with {Organization}", true of either seat; with no name, "a seat with this
              organization" (CBG-FIX111, row 1.3), since "here" on this page is the account. */}
          <p className={STEP_BODY}>
            {signedInAs ?? 'This account'} already has a seat with{' '}
            {organizationName ?? 'this organization'}, so your seat can’t move to it. Create a new
            Givmo account, or sign in with another one.
          </p>
          <TwoWaysOn onCreate={createNewAccount} onAnother={useAnotherAccount} />
        </StepCard>
      ) : null}

      {/* b4m: the account's own confirmed address is the seat's, so no code. */}
      {preview.emailMatchesSeat && (step.kind === 'idle' || step.kind === 'moving') ? (
        <StepCard testId="director-move-match">
          <p className={STEP_BODY}>You’re signed in as {signedInAs}, the address on your seat.</p>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={step.kind === 'moving'}
            onClick={() => void complete()}
            testId="director-move-here"
          >
            Move my seat here
          </Button>
          {step.kind === 'idle' && step.linkUnavailable !== undefined ? (
            <ErrorMessage testId="director-move-link-unavailable">
              {step.linkUnavailable}
            </ErrorMessage>
          ) : null}
          <UseAnotherAccount
            onClick={useAnotherAccount}
            testId="director-move-use-another-account"
          />
        </StepCard>
      ) : null}

      {/* b4: a code to the seat's address, never to the account asking. */}
      {(!preview.emailMatchesSeat && step.kind === 'idle') || step.kind === 'code-ask' ? (
        <StepCard testId="director-move-code-ask">
          <p className={STEP_BODY}>
            You’re signed in as {signedInAs}. We’ll email a 6-digit code to{' '}
            {step.kind === 'code-ask' ? step.masked : masked}, the address on your seat, to confirm
            the move is yours.
          </p>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={step.kind === 'code-ask' && step.sending}
            onClick={() => void requestCode()}
            testId="director-move-send-code"
          >
            Email me a code
          </Button>
          <UseAnotherAccount
            onClick={useAnotherAccount}
            testId="director-move-use-another-account"
          />
        </StepCard>
      ) : null}

      {/* b5, b5w: the invitation's code step, word for word, with the move on its button. */}
      {step.kind === 'code-step' ? (
        <CodeStep
          key={step.sentAt}
          masked={step.masked}
          failures={step.failures}
          sentAt={step.sentAt}
          submitting={step.submitting}
          throttled={step.throttled === true}
          action="Move my seat"
          testIdPrefix="director-move"
          notice={step.linkUnavailable ?? null}
          onSubmit={(code) => void complete(code)}
          onResend={() => void requestCode()}
          onUseAnotherAccount={useAnotherAccount}
        />
      ) : null}

      {step.kind === 'code-unsent' ? (
        <StepCard testId="director-move-code-unsent">
          <p className={STEP_BODY}>
            {step.reason === 'failed'
              ? 'We couldn’t email a code just now. Try again in a moment.'
              : 'We can’t email a code from here. Ask whoever invited you to check the address on your seat.'}
          </p>
          {step.reason === 'failed' ? (
            <Button
              appearance="saas"
              tone="primary"
              size="lg"
              fullWidth
              onClick={() => void requestCode()}
              testId="director-move-send-code"
            >
              Email me a code
            </Button>
          ) : null}
          <UseAnotherAccount
            onClick={useAnotherAccount}
            testId="director-move-use-another-account"
          />
        </StepCard>
      ) : null}
    </DirectorShell>
  );
}

/** b9a, b9b and an account seated here: "Create new account", leading, and the other way. */
function TwoWaysOn({ onCreate, onAnother }: { onCreate: () => void; onAnother: () => void }) {
  return (
    <div className="flex flex-col gap-12">
      <Button
        appearance="saas"
        tone="primary"
        size="lg"
        fullWidth
        onClick={onCreate}
        testId="director-move-create-account"
      >
        Create new account
      </Button>
      <Button
        appearance="saas"
        tone="secondary"
        size="lg"
        fullWidth
        onClick={onAnother}
        testId="director-move-use-another-givmo-account"
      >
        Use another Givmo account
      </Button>
    </div>
  );
}

// ── A failure, and the move ended (b9c) ────────────────────────────────────────────────────────

/** A read or act that failed for no reason the move names: one line and one way to try again. */
function MoveFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <DirectorShell title="Move your seat to another Givmo account" testId={PAGE}>
      <StepCard testId="director-move-error">
        {/* Not drawn; the invitation's own failure, for the move. */}
        <p className={STEP_BODY}>We couldn’t open this move just now.</p>
        <Button
          appearance="saas"
          tone="secondary"
          className="self-start"
          onClick={onRetry}
          testId="director-move-retry"
        >
          Try again
        </Button>
      </StepCard>
    </DirectorShell>
  );
}

function MoveEnded({ signedIn }: { signedIn: boolean }) {
  const host = useBoardDirectorHost();
  const basePath = useBoardBasePath();
  return (
    <DirectorShell
      title="This move has ended"
      description="Nothing moved. A move has to finish within 30 minutes of starting."
      testId={PAGE}
    >
      {/* The invitation's own quiet line under a refusal (its "Switch account" page), at its size. */}
      <p
        className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
        {...testIdProp('director-move-ended')}
      >
        {/* CBG-FIX106 (row 4.14): the seat may be a committee's, and this page can't tell. */}
        Start again, signed in with the account your seat is on.{' '}
        {signedIn ? (
          // The invitation's own "Switch account": signed out, to the door.
          <button type="button" className={STEP_LINK} onClick={() => void host.signOut()}>
            Switch account
          </button>
        ) : (
          <Link
            to={BOARD_DIRECTOR_PAGES.signIn}
            search={{ redirect: boardPath(basePath, BOARD_DIRECTOR_PAGES.home) }}
            className={STEP_LINK}
          >
            Sign in
          </Link>
        )}
      </p>
    </DirectorShell>
  );
}
